// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useModal } from '@shared/ui-kit/hooks';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSilverAlphaProposals } from '../../hooks/use-silver-alpha-proposals';

export type SilverAlphaTab = 'home' | 'documents';

const isDocumentsPath = (pathname: string): boolean =>
  pathname === '/silver-alpha/documents' ||
  pathname.startsWith('/silver-alpha/documents/');

export const useSilverAlphaTopbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasActiveProposal } = useSilverAlphaProposals();
  const [LeaveModalRenderer, leaveModal] = useModal({
    component: 'dialog',
  });

  const activeTab = useMemo<SilverAlphaTab>(
    () => (isDocumentsPath(location.pathname) ? 'documents' : 'home'),
    [location.pathname],
  );

  const titleKey =
    activeTab === 'documents'
      ? ('silver-alpha-tabs.documents' as const)
      : ('silver-alpha-tabs.home' as const);

  useEffect(() => {
    if (activeTab === 'documents' && !hasActiveProposal) {
      navigate('/silver-alpha', { replace: true });
    }
  }, [activeTab, hasActiveProposal, navigate]);

  const onConfirmLeave = useCallback(() => {
    leaveModal.onClose();
    navigate('/aggregation');
  }, [leaveModal.onClose, navigate]);

  const onSelectHome = useCallback(() => {
    navigate('/silver-alpha');
  }, [navigate]);

  const onSelectDocuments = useCallback(() => {
    navigate('/silver-alpha/documents');
  }, [navigate]);

  return {
    LeaveModalRenderer,
    activeTab,
    hasActiveProposal,
    titleKey,
    onBackClick: leaveModal.onOpen,
    onCancelLeave: leaveModal.onClose,
    onConfirmLeave,
    onSelectHome,
    onSelectDocuments,
  };
};
